import { Component, Input, OnChanges, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EdeskService, saveBlobAs } from '../../core/edesk.service';
import { MessageDetail } from '../../api/model/messageDetail';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'app-message-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './message-detail.component.html',
})
export class MessageDetailComponent implements OnChanges, OnDestroy {
  private authService = inject(AuthService);
  
  @Input() messageId: number | null = null;
  @Input() login: string | null = null; // Zmenene na nullable
  @Input() messageClass: string | null = null; // Message class for conditional features

  detail = signal<MessageDetail | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  safeBody = signal<SafeHtml | null>(null);
  sender = signal<string | null>(null);
  recipient = signal<string | null>(null);
  authorizing = signal(false);
  authorizeSuccess = signal<string | null>(null);

  private cancel$ = new Subject<void>();

  constructor(private api: EdeskService, private sanitizer: DomSanitizer) {}

  private isAuthorizationSuccessful(response: any): boolean {
    const toBool = (value: any): boolean | null => {
      if (value === true || value === 'true') return true;
      if (value === false || value === 'false') return false;

      if (value === 0 || value === '0') return true;
      if (typeof value === 'number') return value === 0 ? true : false;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '0') return true;
        if (/^-?\d+$/.test(trimmed)) return Number(trimmed) === 0;
      }

      return null;
    };

    if (response == null) return false;

    if (typeof response === 'string') {
      const direct = toBool(response);
      if (direct !== null) return direct;

      const xmlMatch = response.match(/<\s*ReceiveResult\s*>\s*(-?\d+)\s*<\s*\/\s*ReceiveResult\s*>/i);
      if (xmlMatch?.[1] != null) return Number(xmlMatch[1]) === 0;
    }

    const directResult = toBool(response);
    if (directResult !== null) return directResult;

    const candidates = [
      response?.SuccessfullyAuthorized,
      response?.successfullyAuthorized,
      response?.ReceiveResult,
      response?.receiveResult,
      response?.Result,
      response?.result,
      response?.ReceiveResponse?.ReceiveResult,
      response?.ReceiveResponse?.receiveResult,
      response?.receiveResponse?.ReceiveResult,
      response?.receiveResponse?.receiveResult,
    ];

    for (const candidate of candidates) {
      const normalized = toBool(candidate);
      if (normalized !== null) return normalized;
    }

    return false;
  }

  ngOnDestroy(): void {
    this.cancel$.next();
    this.cancel$.complete();
  }

  private decodeHtml(html: string): string {
    const ta = document.createElement('textarea');
    ta.innerHTML = html;
    return ta.value;
  }

  private resolvePartyDisplay(uri: string | null | undefined) {
    if (!uri) {
      return of(null);
    }

    return this.api.getRecipients(uri, 10).pipe(
      map((recipients) => {
        const match = recipients.find(
          (recipient) => recipient.id?.toLowerCase() === uri.toLowerCase()
        );

        if (!match) {
          return uri;
        }

        return match.name?.trim() || uri;
      }),
      catchError((err) => {
        console.error('Failed to resolve recipient from recipient list:', uri, err);
        return of(uri);
      })
    );
  }

  ngOnChanges(): void {
    // cancel any in-flight requests first
    this.cancel$.next();

    if (!this.messageId) {
      // reset fully when nothing is selected
      this.detail.set(null);
      this.error.set(null);
      this.loading.set(false);
      this.safeBody.set(null);
      this.sender.set(null);
      this.recipient.set(null);
      return;
    }

    // hide previous message immediately while loading the next one
    this.detail.set(null);
    this.safeBody.set(null);
    this.sender.set(null);
    this.recipient.set(null);
    this.error.set(null);
    this.loading.set(true);

    this.api
      .getMessageDetail(this.messageId)
      .pipe(takeUntil(this.cancel$))
      .subscribe({
        next: (response) => {
          // Unwrap the new MessageDetail wrapper
          const d = response.MessageDetail ?? (response as any);
          console.log('Message detail received:', d);
          console.log('Body field:', d.Body);
          console.log('Body type:', typeof d.Body);
          console.log('All keys:', Object.keys(d));
          
          // --- ATTACHMENTS-ONLY FILTER ---
          const rawParts = Array.isArray(d.Parts) ? d.Parts : [];
          const partsOnlyAttachments = rawParts.filter((p: any) => {
            const kind = String(p?.Class ?? p?.Type ?? '')
              .toLowerCase()
              .trim();
            if (!kind) return false; // require explicit type/class
            if (kind.includes('form')) return false; // exclude any form types
            if (kind === 'attachment' || kind === 'attachement') return true;
            return kind.includes('attach'); // fallback: anything that mentions attach
          });

          const msg: MessageDetail = {
            ...d,
            Body: d.Body ?? '', // Handle null body from backend
            Parts: partsOnlyAttachments,
          };

          // Decode and set the body first
          const decoded = msg.Body ? this.decodeHtml(msg.Body) : '';
          this.safeBody.set(
            this.sanitizer.bypassSecurityTrustHtml(decoded)
          );
          
          // Set the detail immediately so it displays
          this.detail.set(msg);
          this.loading.set(false);

          // Show raw URIs immediately, then replace them with recipient-list names on exact match.
          this.sender.set(msg.SenderId ?? null);
          this.recipient.set(msg.RecipientId ?? null);

          const sender$ = this.resolvePartyDisplay(msg.SenderId);
          const recipient$ = this.resolvePartyDisplay(msg.RecipientId);

          forkJoin([sender$, recipient$])
            .pipe(takeUntil(this.cancel$))
            .subscribe({
              next: ([s, r]) => {
                console.log('Recipients resolved - Sender:', s, 'Recipient:', r);
                if (s !== null) this.sender.set(s);
                if (r !== null) this.recipient.set(r);
              },
              error: (err) => {
                console.error('Recipient lookups failed', err);
                // Don't set error here, the message is already displayed
              },
            });
        },
        error: (err) => {
          console.error('getMessageDetail failed', err);
          this.error.set('Skúste to prosím znova alebo kontaktujte podporu.');
          this.loading.set(false);
        },
      });
  }

  download(partId: string | null | undefined, fileName?: string) {
    if (!this.messageId || !partId) return;
    
    this.api
      .downloadMessagePart(this.messageId, partId)
      .pipe(takeUntil(this.cancel$))
      .subscribe({
        next: (resp: HttpResponse<Blob>) =>
          saveBlobAs(resp, fileName || 'priloha'),
        error: (err) => {
          console.error('Download failed', err);
          alert('Nepodarilo sa stiahnuť prílohu.');
        },
      });
  }

  authorize() {
    if (!this.messageId) return;
    
    this.authorizing.set(true);
    this.authorizeSuccess.set(null);
    this.error.set(null);
    
    this.api
      .authorizeMessage(this.messageId)
      .pipe(
        takeUntil(this.cancel$),
        finalize(() => this.authorizing.set(false))
      )
      .subscribe({
        next: (response) => {
          if (this.isAuthorizationSuccessful(response)) {
            this.authorizeSuccess.set('Správa bola úspešne autorizovaná.');
          } else {
            this.error.set('Autorizácia zlyhala.');
          }
        },
        error: (err) => {
          console.error('Authorization failed', err);
          this.error.set('Nepodarilo sa autorizovať správu.');
        },
      });
  }

  isDeliveryNotification(): boolean {
    return this.messageClass === 'ED_DELIVERY_NOTIFICATION';
  }
}
